/**
 * Admin page privilege catalog — single source for Roles UI + route/nav gates.
 * Each page has View plus the write actions that page actually uses (Add / Edit / Delete / …).
 */

export type PrivilegeActionKind = 'add' | 'edit' | 'delete' | 'export' | 'assign' | 'approve' | 'verify';

export interface PrivilegeActionDef {
  kind: PrivilegeActionKind;
  label: string;
  permission: string;
}

export interface AdminPagePrivilege {
  id: string;
  sectionId: string;
  sectionLabel: string;
  sectionAccent?: string;
  pageLabel: string;
  path: string;
  /** Primary permission that unlocks viewing this screen */
  viewPermission: string;
  /**
   * Legacy single write key (older “Manage”). If present on a role, all
   * page actions are treated as granted until they are saved granularly.
   */
  managePermission?: string;
  /** Write actions this screen actually exposes. Empty / omitted = view only. */
  actionKinds?: PrivilegeActionKind[];
  /** Override derived permissions when they should not follow viewPermission. */
  actions?: PrivilegeActionDef[];
  description?: string;
}

const ACTION_META: Record<PrivilegeActionKind, { label: string; suffix: string; color: 'success' | 'info' | 'error' | 'warning' | 'secondary' }> = {
  add: { label: 'Add', suffix: 'create', color: 'success' },
  edit: { label: 'Edit', suffix: 'update', color: 'info' },
  delete: { label: 'Delete', suffix: 'delete', color: 'error' },
  export: { label: 'Export', suffix: 'export', color: 'warning' },
  assign: { label: 'Assign', suffix: 'assign', color: 'secondary' },
  approve: { label: 'Approve', suffix: 'approve', color: 'success' },
  verify: { label: 'Verify', suffix: 'verify', color: 'success' },
};

export function privilegeActionMeta(kind: PrivilegeActionKind) {
  return ACTION_META[kind];
}

export function replacePermissionAction(permission: string, action: string): string {
  const parts = permission.split('.');
  if (parts.length < 2) return `${permission}.${action}`;
  parts[parts.length - 1] = action;
  return parts.join('.');
}

export function getPageActions(page: AdminPagePrivilege): PrivilegeActionDef[] {
  if (page.actions?.length) return page.actions;
  return (page.actionKinds || []).map((kind) => ({
    kind,
    label: ACTION_META[kind].label,
    permission: replacePermissionAction(page.viewPermission, ACTION_META[kind].suffix),
  }));
}

export function pagePermissionKeys(page: AdminPagePrivilege): string[] {
  const keys = new Set<string>([page.viewPermission]);
  if (page.managePermission) keys.add(page.managePermission);
  for (const action of getPageActions(page)) keys.add(action.permission);
  return Array.from(keys);
}

const CRUD: PrivilegeActionKind[] = ['add', 'edit', 'delete'];

/** Page ↔ privilege matrix covering the full admin shell. */
export const ADMIN_PAGE_PRIVILEGES: AdminPagePrivilege[] = [
  // Overview
  {
    id: 'dashboard', sectionId: 'overview', sectionLabel: 'Overview', sectionAccent: '#2563eb',
    pageLabel: 'Dashboard', path: '/dashboard', viewPermission: 'settings.dashboard.view',
    description: 'KPI overview and quick links',
  },
  {
    id: 'users', sectionId: 'overview', sectionLabel: 'Overview', sectionAccent: '#2563eb',
    pageLabel: 'Users & Admins', path: '/users', viewPermission: 'settings.users.view',
    managePermission: 'settings.users.update', actionKinds: CRUD,
    description: 'Staff accounts and role assignment',
  },

  // Tenant master
  {
    id: 'tenants', sectionId: 'tenant_master', sectionLabel: 'Tenant Master', sectionAccent: '#6366f1',
    pageLabel: 'Tenants', path: '/tenants', viewPermission: 'settings.tenants.view',
    managePermission: 'settings.tenants.update', actionKinds: CRUD,
    description: 'SaaS tenant lifecycle (super admin)',
  },
  {
    id: 'tenant_plans', sectionId: 'tenant_master', sectionLabel: 'Tenant Master', sectionAccent: '#6366f1',
    pageLabel: 'Subscription Plans', path: '/tenants/subscription-plans', viewPermission: 'settings.tenants.view',
    managePermission: 'settings.tenants.update', actionKinds: CRUD,
    description: 'Platform billing tiers',
  },

  // Operations
  {
    id: 'dispatcher', sectionId: 'operations', sectionLabel: 'Operations', sectionAccent: '#0ea5e9',
    pageLabel: 'Dispatch Center', path: '/dispatcher', viewPermission: 'dispatcher.dashboard.view',
    managePermission: 'dispatcher.assign.view',
    actions: [{ kind: 'assign', label: 'Assign', permission: 'dispatcher.assign' }],
    description: 'Live fleet and job assignment',
  },
  {
    id: 'sos', sectionId: 'operations', sectionLabel: 'Operations', sectionAccent: '#0ea5e9',
    pageLabel: 'SOS Command', path: '/dispatcher/sos', viewPermission: 'dispatcher.sos.view',
    managePermission: 'dispatcher.sos.manage',
    actions: [{ kind: 'edit', label: 'Edit', permission: 'dispatcher.sos.manage' }],
    description: 'Emergency ride alerts',
  },
  {
    id: 'zones', sectionId: 'operations', sectionLabel: 'Operations', sectionAccent: '#0ea5e9',
    pageLabel: 'Zones', path: '/zones', viewPermission: 'settings.masters.view',
    managePermission: 'settings.masters.update', actionKinds: CRUD,
    description: 'Service zones and geo pricing',
  },
  {
    id: 'rental', sectionId: 'operations', sectionLabel: 'Operations', sectionAccent: '#0ea5e9',
    pageLabel: 'Rental Management', path: '/transport/rental-management', viewPermission: 'transportation.rides.view',
    managePermission: 'transportation.rides.update', actionKinds: CRUD,
    description: 'Hour/km rental packages',
  },
  {
    id: 'outstation', sectionId: 'operations', sectionLabel: 'Operations', sectionAccent: '#0ea5e9',
    pageLabel: 'Outstation Management', path: '/transport/outstation-management', viewPermission: 'transportation.rides.view',
    managePermission: 'transportation.rides.update', actionKinds: CRUD,
    description: 'Outstation KM slab pricing',
  },

  // Onboarding
  {
    id: 'partners', sectionId: 'onboarding', sectionLabel: 'Onboarding', sectionAccent: '#7c3aed',
    pageLabel: 'Partners', path: '/partners', viewPermission: 'partners.view',
    managePermission: 'partners.manage', actionKinds: CRUD,
    description: 'Drivers, merchants, providers',
  },
  {
    id: 'partner_plans', sectionId: 'onboarding', sectionLabel: 'Onboarding', sectionAccent: '#7c3aed',
    pageLabel: 'Partner Subscriptions', path: '/partners/subscription-plans', viewPermission: 'partners.view',
    managePermission: 'partners.manage', actionKinds: CRUD,
    description: 'Partner prepaid plans',
  },
  {
    id: 'onboarding_hub', sectionId: 'onboarding', sectionLabel: 'Onboarding', sectionAccent: '#7c3aed',
    pageLabel: 'Onboarding Hub', path: '/onboarding', viewPermission: 'partners.view',
    description: 'Partner registration preview',
  },
  {
    id: 'documents', sectionId: 'onboarding', sectionLabel: 'Onboarding', sectionAccent: '#7c3aed',
    pageLabel: 'Documents', path: '/config/documents', viewPermission: 'partners.view',
    managePermission: 'documents.verify',
    actions: [{ kind: 'verify', label: 'Verify', permission: 'documents.verify' }],
    description: 'Approve partner documents',
  },

  // Service hubs
  {
    id: 'hub_transport', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Transport Hub', path: '/hubs/transport', viewPermission: 'transportation.dashboard.view',
    description: 'Taxi services, rides, drivers',
  },
  {
    id: 'hub_delivery', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Delivery Hub', path: '/hubs/delivery', viewPermission: 'food.orders.view',
    description: 'Food and grocery sub-services',
  },
  {
    id: 'food_orders', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Food Orders', path: '/food/orders', viewPermission: 'food.orders.view',
    managePermission: 'food.orders.update', actionKinds: ['edit', 'assign'],
  },
  {
    id: 'food_restaurants', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Restaurants', path: '/food/restaurants', viewPermission: 'food.masters.view',
    managePermission: 'food.masters.update', actionKinds: CRUD,
  },
  {
    id: 'food_catalog', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Food Catalog', path: '/food/menu', viewPermission: 'food.masters.view',
    managePermission: 'food.masters.update', actionKinds: CRUD,
  },
  {
    id: 'grocery_orders', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Grocery Orders', path: '/grocery/orders', viewPermission: 'grocery.orders.view',
    managePermission: 'grocery.orders.update', actionKinds: ['edit', 'assign'],
  },
  {
    id: 'grocery_stores', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Stores', path: '/grocery/stores', viewPermission: 'grocery.masters.view',
    managePermission: 'grocery.masters.update', actionKinds: CRUD,
  },
  {
    id: 'grocery_products', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Products & Inventory', path: '/grocery/products', viewPermission: 'grocery.masters.view',
    managePermission: 'grocery.masters.update', actionKinds: CRUD,
  },
  {
    id: 'transport_rides', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Rides Dashboard', path: '/transport/rides', viewPermission: 'transportation.rides.view',
    managePermission: 'transportation.rides.update', actionKinds: ['edit', 'assign'],
  },
  {
    id: 'transport_drivers', sectionId: 'service_modules', sectionLabel: 'Service Hubs', sectionAccent: '#0f766e',
    pageLabel: 'Driver Management', path: '/transport/drivers', viewPermission: 'partners.view',
    managePermission: 'partners.manage', actionKinds: CRUD,
  },

  // Customer app
  {
    id: 'home_banners', sectionId: 'customer_app', sectionLabel: 'Customer App', sectionAccent: '#e11d48',
    pageLabel: 'Home Banners', path: '/customer-app/home-banners', viewPermission: 'settings.masters.view',
    managePermission: 'settings.masters.update', actionKinds: CRUD,
  },
  {
    id: 'promos', sectionId: 'customer_app', sectionLabel: 'Customer App', sectionAccent: '#e11d48',
    pageLabel: 'Promos & Coupons', path: '/customer-app/promos-coupons', viewPermission: 'settings.masters.view',
    managePermission: 'settings.masters.update', actionKinds: CRUD,
  },

  // Referrals & Points
  {
    id: 'referral_settings', sectionId: 'rewards', sectionLabel: 'Referrals & Points', sectionAccent: '#16a34a',
    pageLabel: 'Referral Settings', path: '/rewards/referral-settings', viewPermission: 'marketing.rewards.view',
    actionKinds: ['edit'],
  },
  {
    id: 'referrals', sectionId: 'rewards', sectionLabel: 'Referrals & Points', sectionAccent: '#16a34a',
    pageLabel: 'Referrals', path: '/rewards/referrals', viewPermission: 'marketing.rewards.view',
    actions: [{ kind: 'approve', label: 'Approve / reject / retry', permission: 'marketing.rewards.update' }],
  },
  {
    id: 'points_settings', sectionId: 'rewards', sectionLabel: 'Referrals & Points', sectionAccent: '#16a34a',
    pageLabel: 'Points Settings', path: '/rewards/points-settings', viewPermission: 'marketing.rewards.view',
    actionKinds: ['edit'],
  },
  {
    id: 'points_ledger', sectionId: 'rewards', sectionLabel: 'Referrals & Points', sectionAccent: '#16a34a',
    pageLabel: 'Points Ledger', path: '/rewards/points', viewPermission: 'marketing.rewards.view',
    actionKinds: [],
    description: 'Manual point adjustments are limited to super and client admins',
  },
  // Finance
  {
    id: 'settlements', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Settlements', path: '/settlements', viewPermission: 'analytics.reports.view',
    managePermission: 'analytics.reports.export', actionKinds: ['export'],
  },
  {
    id: 'payment_transactions', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Payment Transactions', path: '/payment-transactions', viewPermission: 'analytics.reports.view',
    managePermission: 'analytics.reports.export', actionKinds: ['export'],
  },
  {
    id: 'tenant_transactions', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Transaction History', path: '/tenants/transactions', viewPermission: 'analytics.reports.view',
    actionKinds: [],
    description: 'Card hold and completed payment logs',
  },
  {
    id: 'payout_withdrawals', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Partner Withdrawals', path: '/payouts/withdrawals', viewPermission: 'partners.view',
    actions: [{ kind: 'approve', label: 'Approve / reject / pay / mark paid', permission: 'partners.update' }],
  },
  {
    id: 'payout_kyc', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Payout KYC', path: '/payouts/kyc', viewPermission: 'partners.view',
    actions: [{ kind: 'approve', label: 'Verify / reject KYC', permission: 'partners.update' }],
  },
  {
    id: 'payout_accounts', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Payout Bank Accounts', path: '/payouts/accounts', viewPermission: 'partners.view',
    actions: [{ kind: 'approve', label: 'Verify / reject / disable accounts', permission: 'partners.update' }],
  },
  {
    id: 'payout_reports', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Payout Reports', path: '/payouts/reports', viewPermission: 'analytics.reports.view',
    managePermission: 'analytics.reports.export', actionKinds: ['export'],
  },
  {
    id: 'payout_settings', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Payout Settings', path: '/payouts/settings', viewPermission: 'partners.view',
    actionKinds: [],
    description: 'Changing payout mode and limits is limited to super and client admins',
  },
  {
    id: 'reports', sectionId: 'finance', sectionLabel: 'Finance & Reports', sectionAccent: '#f59e0b',
    pageLabel: 'Reports', path: '/reports', viewPermission: 'analytics.reports.view',
    managePermission: 'analytics.reports.export', actionKinds: ['export'],
  },

  // Support
  {
    id: 'helpdesk', sectionId: 'support', sectionLabel: 'Support', sectionAccent: '#8b5cf6',
    pageLabel: 'Helpdesk', path: '/helpdesk', viewPermission: 'helpdesk.tickets.view',
    managePermission: 'helpdesk.tickets.update', actionKinds: ['edit'],
  },

  // Settings
  {
    id: 'masters', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'Master Data', path: '/config/masters', viewPermission: 'settings.masters.view',
    managePermission: 'settings.masters.update', actionKinds: CRUD,
  },
  {
    id: 'roles', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'Roles & Privileges', path: '/config/roles', viewPermission: 'settings.roles.view',
    managePermission: 'settings.roles.update', actionKinds: CRUD,
    description: 'This privilege screen',
  },
  {
    id: 'notifications', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'Notifications', path: '/config/notifications', viewPermission: 'settings.users.view',
    managePermission: 'settings.users.update', actionKinds: CRUD,
  },
  {
    id: 'payments', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'Payment Gateways', path: '/config/payments', viewPermission: 'settings.settings.view',
    managePermission: 'settings.settings.update', actionKinds: ['edit'],
  },
  {
    id: 'sms', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'SMS Gateways', path: '/config/sms', viewPermission: 'settings.settings.view',
    managePermission: 'settings.settings.update', actionKinds: ['edit'],
  },
  {
    id: 'translations', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'Translator Master', path: '/config/translations', viewPermission: 'settings.masters.view',
    managePermission: 'settings.masters.update', actionKinds: CRUD,
  },
  {
    id: 'app_versioning', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'App Versioning', path: '/config/app-versioning', viewPermission: 'settings.settings.view',
    managePermission: 'settings.settings.update', actionKinds: ['edit'],
  },
  {
    id: 'site_settings', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'Site Settings', path: '/config/settings', viewPermission: 'settings.settings.view',
    managePermission: 'settings.settings.update', actionKinds: ['edit'],
  },
  {
    id: 'audit', sectionId: 'config', sectionLabel: 'Settings', sectionAccent: '#64748b',
    pageLabel: 'Audit Logs', path: '/audit-logs', viewPermission: 'settings.audit.view',
  },
];

export function groupPagePrivilegesBySection(): Array<{
  sectionId: string;
  sectionLabel: string;
  sectionAccent: string;
  pages: AdminPagePrivilege[];
}> {
  const map = new Map<string, { sectionId: string; sectionLabel: string; sectionAccent: string; pages: AdminPagePrivilege[] }>();
  for (const page of ADMIN_PAGE_PRIVILEGES) {
    if (!map.has(page.sectionId)) {
      map.set(page.sectionId, {
        sectionId: page.sectionId,
        sectionLabel: page.sectionLabel,
        sectionAccent: page.sectionAccent || '#64748b',
        pages: [],
      });
    }
    map.get(page.sectionId)!.pages.push(page);
  }
  return Array.from(map.values());
}

/** Unique permission keys used by the page matrix (for seed / catalog sync). */
export function collectPagePrivilegeKeys(): string[] {
  const keys = new Set<string>();
  for (const p of ADMIN_PAGE_PRIVILEGES) {
    for (const key of pagePermissionKeys(p)) keys.add(key);
  }
  return Array.from(keys).sort();
}
